/**
 * useChatSession Hook
 * Manages chat session creation, selection, and Firestore subscriptions
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getDb, createChatSession as createChatSessionUtil, sendWelcomeMessage } from "../components/chatdialog/utils/firestoreUtils";
import { formatBrandCopy } from "../utils/brandCopy";

/**
 * Hook to manage chat sessions
 * @param {string} userId - User ID
 * @param {boolean} isOpen - Whether the dialog is open
 * @returns {Object} { sessions, selectedChatId, setSelectedChatId, createChat, selectChat, deleteChat, loading, error }
 */
export const useChatSession = (userId, isOpen) => {
        const { t } = useTranslation();
        const [sessions, setSessions] = useState([]);
        const [selectedChatId, setSelectedChatId] = useState(null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null);
        const [initializingChat, setInitializingChat] = useState(false);

        const creatingChatRef = useRef(null);
        const db = getDb();

        /**
         * Create a new chat session
         * @returns {Promise<string|null>} New chat session ID or null
         */
        const createChat = useCallback(async () => {
                if (!userId) {
                        return null;
                }

                if (creatingChatRef.current) {
                        return creatingChatRef.current;
                }

                try {
                        const newChatId = await createChatSessionUtil(userId);
                        creatingChatRef.current = newChatId;

                        setSelectedChatId(newChatId);

                        const welcomeText = formatBrandCopy(
                                t("ai-chat-welcome", {
                                        defaultValue: "Hello! I'm Lisa, your AI assistant. I can help with animal management, treatments, predictions, and farm operations.",
                                })
                        );
                        
                        await sendWelcomeMessage(userId, newChatId, welcomeText);

                        return newChatId;
                } catch (createError) {
                        console.error("[useChatSession][createChat] - Error creating chat:", createError);
                        setError(t("ai-chat-error-loading"));
                        return null;
                } finally {
                        creatingChatRef.current = null;
                }
        }, [userId, t]);

        /**
         * Select a chat session
         * @param {string} chatId - Chat session ID to select
         */
        const selectChat = useCallback((chatId) => {
                setSelectedChatId(chatId);
        }, []);

        /**
         * Delete a chat session
         * @param {string} chatId - Chat session ID to delete
         * @returns {Promise<void>}
         */
        const deleteChat = useCallback(async (chatId) => {
                if (!userId || !chatId) {
                        console.warn("[useChatSession][deleteChat] - Missing userId or chatId", { userId, chatId });
                        return;
                }

                try {
                        setLoading(true);

                        const snapshot = await db
                                .collection("Users")
                                .doc(userId)
                                .collection("AIChat")
                                .doc(chatId)
                                .collection("Messages")
                                .get();

                        const batchSize = 450;
                        let batch = db.batch();
                        let operationCount = 0;

                        for (const doc of snapshot.docs) {
                                batch.delete(doc.ref);
                                operationCount++;

                                if (operationCount >= batchSize) {
                                        await batch.commit();
                                        batch = db.batch();
                                        operationCount = 0;
                                }
                        }

                        if (operationCount > 0) {
                                await batch.commit();
                        }

                        await db
                                .collection("Users")
                                .doc(userId)
                                .collection("AIChat")
                                .doc(chatId)
                                .delete();

                        if (selectedChatId === chatId) {
                                setSelectedChatId(null);
                        }

                        if (sessions.length <= 1) {
                                await createChat();
                        }
                } catch (deleteError) {
                        console.error("[useChatSession][deleteChat] - Error deleting chat:", deleteError);
                        alert(t("ai-chat-history-delete-error") || "Failed to delete this chat.");
                } finally {
                        setLoading(false);
                }
        }, [userId, selectedChatId, sessions.length, db, createChat, t]);

        // Load chat sessions and keep history synced
        useEffect(() => {
                if (!isOpen || !userId) {
                        return;
                }

                setInitializingChat(true);
                setError(null);

                const unsubscribe = db
                        .collection("Users")
                        .doc(userId)
                        .collection("AIChat")
                        .orderBy("updatedAt", "desc")
                        .onSnapshot(async (snapshot) => {
                                const newSessions = snapshot.docs.map((doc) => ({
                                        id: doc.id,
                                        ...doc.data(),
                                }));

                                setSessions(newSessions);

                                if (newSessions.length === 0) {
                                        setSelectedChatId(null);
                                        setInitializingChat(false);
                                        await createChat();
                                        return;
                                }

                                if (!selectedChatId || !newSessions.some((session) => session.id === selectedChatId)) {
                                        setSelectedChatId(newSessions[0].id);
                                }

                                setInitializingChat(false);
                        }, (snapshotError) => {
                                console.error("[useChatSession][sessionsSnapshot] - Error loading chat sessions:", snapshotError);
                                setError(t("ai-chat-error-loading"));
                                setInitializingChat(false);
                        });

                return () => unsubscribe();
        }, [userId, createChat, isOpen, selectedChatId, db, t]);

        return {
                sessions,
                selectedChatId,
                setSelectedChatId,
                createChat,
                selectChat,
                deleteChat,
                loading: loading || initializingChat,
                error,
        };
};

